<template lang="pug" src="./commission-editor.pug"></template>
<style lang="scss" src="./commission-editor.scss" scoped></style>

<script>
import animateScrollTo from 'animated-scroll-to';
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { demical } from '@/utils/helpers';
import validate from '@/mixins/validate';

import request from '@/utils/request';

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        commission: {
            title: null,
            fixedPerOrder: null,
            fixedPerDay: null,
            perDayType: 'since-first-login',
            fixedPerWeek: 0,
            fixedPerMonth: 0,
            deliveryDelay: null,
            isDefault: false,
            brandId: null,
            hasPrecedenceForForced: false
        },
        typesOfCommission: ['has-orders', 'has-orders-count-per-five', 'since-first-login'],
        borders: [],
        border: {
            leftBorder: null,
            rightBorder: null,
            staticValue: null,
            percentValue: null
        },
        transferCommissionId: null,
        disabledDefaultCommission: false,
        canSaveCommission: false,
        canSaveBorder: false,
        loaded: false,
        removeModal: false,
        transferModal: false,
        requestProcessing: false
    }),
    validations: {
        commission: {
            title: {
                required
            },
            fixedPerOrder: {
                required,
                demical
            },
            fixedPerDay: {
                required,
                demical
            },
            perDayType: {
                required
            },
            fixedPerWeek: {
                required,
                demical
            },
            fixedPerMonth: {
                required,
                demical
            },
            deliveryDelay: {
                required,
                demical
            },
            brandId: {
                required
            }
        },
        border: {
            leftBorder: {
                required,
                numeric: function(value) {
                    return Number(value).toFixed(0) == value;
                }
            },
            rightBorder: {
                required,
                numeric: function(value) {
                    return Number(value).toFixed(0) == value;
                },
                minValue: function(value) {
                    return value > Number(this.border.leftBorder);
                }
            },
            staticValue: {
                required,
                demical
            },
            percentValue: {
                required,
                demical
            }
        },
        transferCommissionId: {
            required
        }
    },
    computed: {
        ...mapGetters(['userBrandId']),
        ...mapState({
            brands: state => state.data.brands,
            commissions: function(state) {
                const commissions = state.data.driverCommissions;

                if (!commissions.length || !this.commission.brandId) {
                    return [];
                }

                return commissions.filter(it =>
                    it.brandId == this.commission.brandId &&
                    it.id != this.commission.id
                );
            }
        }),
        saveMethod() {
            return this.commission.id ? 'put' : 'post';
        },
        saveUrl() {
            return this.commission.id ? `/admin/driver/commissions/${this.commission.id}` : '/admin/driver/commissions';
        },
        headers() {
            return ['leftBorder', 'rightBorder', 'staticValue', 'percentValue', 'actions'].map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.Commissions.borders.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        }
    },
    watch: {
        commission: {
            handler: function() {
                this.canSaveCommission = true;
            },
            deep: true
        },
        border: {
            handler: function() {
                this.canSaveBorder = true;
            },
            deep: true
        }
    },
    created() {
        if (this.userBrandId) {
            this.commission.brandId = this.userBrandId;
        }

        if (this.$route.params.id) {
            this.loaded = true;

            this.loadData().then(() => {
                this.loadCommissionBorders();
                this.$store.dispatch('data/getDriverCommissions');
            });
        }

        this.$store.dispatch('data/getBrands');
    },
    methods: {
        async loadData() {
            await request({ method: 'get', url: `/admin/driver/commissions/${this.$route.params.id}` })
                .then(res => this.successLoad(res)).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                });
        },

        async loadCommissionBorders() {
            await request({ method: 'get', url: `/admin/commission/borders/${this.commission.id}` })
                .then(res => {
                    this.borders = res.data;
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                });
        },

        async saveCommission() {
            if (this.$v.commission.$invalid) {
                this.$v.commission.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign({}, this.commission);

            await request({ method: this.saveMethod, url: this.saveUrl, data })
                .then(res => {
                    this.successLoad(res);

                    if (!this.$route.params.id) {
                        this.$router.push(`/finances/commissions/editor/${this.commission.id}`);
                    }

                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        successLoad(res) {
            this.commission = Object.assign({}, this.commission, res.data);
            this.disabledDefaultCommission = this.commission.isDefault;

            this.$nextTick(() => {
                this.canSaveCommission = false;
                this.loaded = false;
            });
        },

        async remove() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({ method: 'delete', url: `/admin/driver/commissions/${this.commission.id}` })
                .then(() => {
                    this.$router.push(`/finances/commissions`);
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                        this.removeModal = false;
                    });

                });
        },

        editBorder: function(border) {
            if (!border.id) {
                return false;
            }

            this.border = Object.assign({}, this.border, border);

            this.$nextTick(() => {
                this.canSaveBorder = false;
                this.focusBorderForm();
            });
        },

        focusBorderForm: function() {
            const element = document.querySelector('.v-application').querySelector('#border-form');

            if (element) {
                const domRect = element.getBoundingClientRect();

                animateScrollTo(domRect.top + document.documentElement.scrollTop + 400, {
                    speed: 500,
                    minDuration: 500,
                    maxDuration: 1500,
                    element: window,
                    cancelOnUserAction: true
                });
            }
        },

        async saveBorder() {
            if (this.$v.border.$invalid) {
                this.$v.border.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign({}, this.border, {
                commissionId: this.commission.id
            });

            const method = data.id ? 'put' : 'post';
            const url = data.id ? `/admin/commission/borders/${data.id}` : '/admin/commission/borders';

            await request({ method, url, data })
                .then(() => {
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });

                    this.loadCommissionBorders();
                }).catch(error => {
                    if (error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        [
                            'id', 'leftBorder', 'rightBorder',
                            'staticValue', 'percentValue'
                        ].forEach(it => {
                            this.border[it] = null;
                        });

                        this.$v.border.$reset();
                        this.requestProcessing = false;
                        this.canSaveBorder = false;
                    });
                });
        },

        async removeBorder() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({ method: 'delete', url: `/admin/commission/borders/${this.border.id}` })
                .then(() => {
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                    this.loadCommissionBorders();
                    this.clearBorderForm();
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => this.requestProcessing = false);
                });
        },

        clearBorderForm: function() {
            this.border = Object.assign({}, {
                leftBorder: null,
                rightBorder: null,
                staticValue: null,
                percentValue: null
            });

            this.$nextTick(() => {
                this.$v.border.$reset();
            });
        },

        openTransferDialog() {
            if (this.$v.transferCommissionId.$invalid) {
                this.$v.transferCommissionId.$touch();
                return;
            }

            this.transferModal = true;
        },

        async transferDrivers() {
            await request({
                method: 'put',
                url: `/admin/commission/assign/${this.commission.id}/${this.transferCommissionId}`
            }).then(res => {
                if (res.status == 204) {
                    this.$store.dispatch('alert/addMessage', {
                        message: 'success',
                        type: 'success'
                    });

                    this.transferCommissionId = null;
                    this.$set(this.commission, 'driversCount', 0);

                    this.$nextTick(() => {
                        this.transferModal = false;
                        this.$v.transferCommissionId.$reset();
                    });
                }
            }).catch(error => this.errorHandler(error));
        },

        errorHandler(error) {
            const message = error.response && error.response.data && error.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    message
                });
            }
        }
    }
};
</script>
